(() => {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════════
     SizeGuide custom element for Drawer (PDP)
     ─ Handles horizontal scroll wrapping, arrow paging, and Cm/Inch toggling
     ─ Observes Dialog open states and DOM mutations (Shopify variant updates)
  ══════════════════════════════════════════════════════════════════════ */
  class SizeGuide extends HTMLElement {

    connectedCallback() {
      this._destroy();

      this._defaultUnit     = this.dataset.defaultUnit || 'cm';
      this._currentUnit     = this._defaultUnit;
      this._resizeObservers = [];

      this._bindEvents();
      
      // Run initial setup after paint/layout
      requestAnimationFrame(() => this._initialSetup());

      // Watch for Shopify replacing our innerHTML on variant changes
      this._mutationObserver = new MutationObserver((mutations) => {
        let hasNewContent = false;
        for (const m of mutations) {
          if (m.type === 'childList' && m.addedNodes.length) {
            hasNewContent = true;
            break;
          }
        }
        if (!hasNewContent) return;

        // Re-run setup on new HTML content
        requestAnimationFrame(() => this._initialSetup());
      });
      this._mutationObserver.observe(this, { childList: true, subtree: false });

      // Safety net: re-run setup when the dialog opens
      this._bindDialogOpenListener();
    }

    disconnectedCallback() {
      this._destroy();
    }

    _destroy() {
      this._resizeObservers?.forEach(ro => ro.disconnect());
      this._resizeObservers = [];
      this._mutationObserver?.disconnect();
      this._mutationObserver = null;
      if (this._boundClick) {
        this.removeEventListener('click', this._boundClick);
        this._boundClick = null;
      }
      if (this._dialogEl && this._boundDialogToggle) {
        this._dialogEl.removeEventListener('toggle', this._boundDialogToggle);
      }
      this._dialogEl = null;
      this._boundDialogToggle = null;
      if (this._dialogAttrObserver) {
        this._dialogAttrObserver.disconnect();
        this._dialogAttrObserver = null;
      }
    }

    // ════════════════════════════════════════════════════════════════════
    // Events & Initializer
    // ════════════════════════════════════════════════════════════════════

    _bindEvents() {
      this._boundClick = this._onClick.bind(this);
      this.addEventListener('click', this._boundClick);
    }

    _initialSetup() {
      // Find all raw tables inside the active chart's container
      const tables = this.querySelectorAll('.sgc-tables table');
      if (tables.length === 0) return;

      const scrollbarTemplate = document.getElementById('size-guide-scrollbar-template');

      tables.forEach((table) => {
        if (table.dataset.enhanced === '1') return;
        table.dataset.enhanced = '1';

        // Reconstruct DOM to support horizontal scrolling:
        const item = document.createElement('div');
        item.className = 'sgc-table-item';

        const wrapper = document.createElement('div');
        wrapper.className = 'sgc-table-wrapper';

        const scrollEl = document.createElement('div');
        scrollEl.className = 'sgc-table-scroll';
        scrollEl.dataset.sizeGuideScroll = '';
        scrollEl.setAttribute('tabindex', '0');

        table.parentNode.insertBefore(item, table);
        scrollEl.appendChild(table);
        wrapper.appendChild(scrollEl);
        item.appendChild(wrapper);

        // Clone the hidden scrollbar template from Liquid
        if (scrollbarTemplate) {
          const scrollbar = scrollbarTemplate.firstElementChild.cloneNode(true);
          scrollbar.removeAttribute('hidden');
          item.appendChild(scrollbar);
        }

        // Initialize scroll tracking for this container
        this._initScrollTracking(scrollEl);
      });

      // Synchronize unit toggle buttons and table container to match this._currentUnit
      this._applyUnitState(this._currentUnit);
    }

    _onClick(event) {
      const unitBtn  = event.target.closest('[data-unit]');
      const arrowBtn = event.target.closest('[data-scroll-dir]');
      if (unitBtn)  this._handleUnitToggle(unitBtn);
      if (arrowBtn) this._handleScrollArrow(arrowBtn);
    }

    _handleUnitToggle(btn) {
      const unit = btn.dataset.unit;
      this._currentUnit = unit;
      this._applyUnitState(unit);
    }

    _applyUnitState(unit) {
      const inner = this.querySelector('.sgc-inner');
      if (inner) {
        inner.setAttribute('data-active-unit', unit);
      }

      this.querySelectorAll('.sgc-unit-btn').forEach((b) => {
        const active = b.dataset.unit === unit;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', String(active));
      });

      // Update overflow arrow states for the visible table(s) in active unit
      const activeUnitTable = this.querySelector(`[data-unit="${unit}"]`);
      if (activeUnitTable) {
        activeUnitTable.querySelectorAll('[data-size-guide-scroll]').forEach((scrollEl) => {
          this._updateArrowStates(scrollEl);
        });
      }
    }

    _handleScrollArrow(btn) {
      if (btn.disabled) return;
      const item     = btn.closest('.sgc-table-item');
      const scrollEl = item?.querySelector('[data-size-guide-scroll]');
      if (!scrollEl) return;
      const step = Math.max(scrollEl.clientWidth * 0.6, 120);
      scrollEl.scrollBy({ left: Number(btn.dataset.scrollDir) * step, behavior: 'smooth' });
    }

    // ════════════════════════════════════════════════════════════════════
    // Scroll tracking & Arrows updating
    // ════════════════════════════════════════════════════════════════════

    _initScrollTracking(scrollEl) {
      if (scrollEl._sgScrollInit) return;
      scrollEl._sgScrollInit = true;

      const update = () => this._updateArrowStates(scrollEl);
      scrollEl.addEventListener('scroll', update, { passive: true });

      if (window.ResizeObserver) {
        const ro = new ResizeObserver(update);
        ro.observe(scrollEl);
        this._resizeObservers.push(ro);
      }

      requestAnimationFrame(() => requestAnimationFrame(update));
    }

    _updateArrowStates(scrollEl) {
      const item = scrollEl.closest('.sgc-table-item');
      if (!item) return;

      const { scrollLeft, clientWidth, scrollWidth } = scrollEl;
      const atStart     = scrollLeft <= 1;
      const atEnd       = scrollLeft + clientWidth >= scrollWidth - 1;
      const hasOverflow = scrollWidth > clientWidth + 1;

      const bar = item.querySelector(':scope > .sgc-scroll-bar');
      if (bar) bar.hidden = !hasOverflow;
      if (!hasOverflow) {
        item.querySelector(':scope > .sgc-scroll-bar [data-scroll-dir="-1"]')
          ?.setAttribute('disabled', '');
        item.querySelector(':scope > .sgc-scroll-bar [data-scroll-dir="1"]')
          ?.setAttribute('disabled', '');
        return;
      }

      item.querySelector(':scope > .sgc-scroll-bar [data-scroll-dir="-1"]')
        ?.toggleAttribute('disabled', atStart);
      item.querySelector(':scope > .sgc-scroll-bar [data-scroll-dir="1"]')
        ?.toggleAttribute('disabled', atEnd);
    }

    // ════════════════════════════════════════════════════════════════════
    // Dialog-open safety net
    // ════════════════════════════════════════════════════════════════════

    _bindDialogOpenListener() {
      const dialogEl = this.closest('dialog');
      if (!dialogEl) return;

      this._dialogEl = dialogEl;
      this._boundDialogToggle = (e) => {
        const isOpen = e?.newState ? e.newState === 'open' : dialogEl.hasAttribute('open');
        if (isOpen) {
          requestAnimationFrame(() => this._initialSetup());
        }
      };

      dialogEl.addEventListener('toggle', this._boundDialogToggle);

      if (!this._dialogAttrObserver) {
        this._dialogAttrObserver = new MutationObserver(() => {
          if (dialogEl.hasAttribute('open')) {
            requestAnimationFrame(() => this._initialSetup());
          }
        });
      }
      this._dialogAttrObserver.observe(dialogEl, { attributes: true, attributeFilter: ['open'] });
    }
  }

  if (!customElements.get('size-guide')) {
    customElements.define('size-guide', SizeGuide);
  }

})();